import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Filter, Loader2 } from 'lucide-react';
import { calendarApi } from '../../services/api';

interface LeaveEvent {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  status: string;
}

interface Holiday {
  date: string;
  name: string;
  type?: string;
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [leaveEvents, setLeaveEvents] = useState<LeaveEvent[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);

  // Fetch calendar data when month changes
  useEffect(() => {
    const fetchCalendarData = async () => {
      setIsLoading(true);
      setError('');

      try {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const startDate = new Date(year, month, 1).toISOString().split('T')[0];
        const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

        // Fetch events and holidays in parallel
        const [eventsResponse, holidaysResponse] = await Promise.all([
          calendarApi.getEvents({ startDate, endDate }).catch(() => ({ events: [] })),
          calendarApi.getHolidays(year.toString()).catch(() => ({ holidays: [] })),
        ]);

        const events = (eventsResponse as { events: LeaveEvent[] }).events || [];
        setLeaveEvents(events);
        setHolidays((holidaysResponse as { holidays: Holiday[] }).holidays || []);

        // Extract unique departments from events
        const uniqueDepts = [...new Set(events.map(e => e.department).filter(Boolean))];
        setDepartments(uniqueDepts.sort());
      } catch (err: any) {
        console.error('Error fetching calendar data:', err);
        setError(err.message || 'Failed to load calendar data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCalendarData();
  }, [currentDate.getFullYear(), currentDate.getMonth()]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty days for the start of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const formatDate = (date: Date) => {
    // Use local date parts to avoid timezone issues with toISOString()
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = formatDate(date);
    let events = leaveEvents.filter(event => {
      return dateStr >= event.startDate && dateStr <= event.endDate;
    });

    // Apply department filter
    if (departmentFilter !== 'all') {
      events = events.filter(e => e.department === departmentFilter);
    }

    return events;
  };

  const getHolidayForDate = (date: Date) => {
    const dateStr = formatDate(date);
    return holidays.find(h => h.date === dateStr);
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const navigateMonth = (direction: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const days = getDaysInMonth(currentDate);
  const monthYear = currentDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });

  const formatLeaveType = (type: string) => {
    return type.charAt(0) + type.slice(1).toLowerCase();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING_APPROVAL':
      case 'PENDING_RECOMMENDATION':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading calendar...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Calendar</h1>
          <p className="text-sm text-gray-600">View team leave and availability</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={goToToday} className="btn-secondary text-sm">
            Today
          </button>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={departmentFilter}
              onChange={e => setDepartmentFilter(e.target.value)}
              className="input w-48"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="card">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <button
            onClick={() => navigateMonth(-1)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">{monthYear}</h2>
          <button
            onClick={() => navigateMonth(1)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="py-2 text-sm font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="min-h-[100px] bg-gray-50" />;
              }

              const events = getEventsForDate(date);
              const holiday = getHolidayForDate(date);
              const weekend = isWeekend(date);
              const isToday = formatDate(date) === formatDate(new Date());

              return (
                <div
                  key={formatDate(date)}
                  className={`min-h-[100px] border p-1 ${
                    weekend ? 'bg-gray-100' : 'bg-white'
                  } ${holiday ? 'bg-red-50' : ''} ${
                    isToday ? 'border-primary-500 border-2' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm font-medium ${
                        isToday
                          ? 'flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-white'
                          : weekend
                            ? 'text-gray-400'
                            : 'text-gray-900'
                      }`}
                    >
                      {date.getDate()}
                    </span>
                  </div>

                  {holiday && (
                    <div className="mt-1 truncate rounded bg-red-100 px-1 py-0.5 text-xs text-red-800">
                      {holiday.name}
                    </div>
                  )}

                  <div className="mt-1 space-y-1">
                    {events.slice(0, 2).map(event => (
                      <div
                        key={event.id}
                        className={`truncate rounded px-1 py-0.5 text-xs ${getStatusColor(event.status)}`}
                        title={`${event.employeeName} - ${formatLeaveType(event.leaveType)} (${event.status})`}
                      >
                        {event.employeeName.split(' ')[0]}
                      </div>
                    ))}
                    {events.length > 2 && (
                      <div className="text-xs text-gray-500">+{events.length - 2} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="card p-4">
        <h3 className="mb-3 text-sm font-medium text-gray-700">Legend</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-green-100" />
            <span className="text-sm text-gray-600">Approved Leave</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-yellow-100" />
            <span className="text-sm text-gray-600">Pending Leave</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-red-100" />
            <span className="text-sm text-gray-600">Public Holiday</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-gray-100" />
            <span className="text-sm text-gray-600">Weekend</span>
          </div>
        </div>
      </div>

      {/* Events Summary */}
      {leaveEvents.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-700">Leave This Month ({leaveEvents.length})</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {leaveEvents
              .filter(e => departmentFilter === 'all' || e.department === departmentFilter)
              .sort((a, b) => a.startDate.localeCompare(b.startDate))
              .map(event => (
                <div key={event.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                  <div>
                    <span className="font-medium">{event.employeeName}</span>
                    <span className="text-gray-500 ml-2">
                      {formatLeaveType(event.leaveType)} ({event.totalDays} day{event.totalDays !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-600">{event.startDate}</span>
                    {event.startDate !== event.endDate && (
                      <span className="text-gray-600"> - {event.endDate}</span>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
